import React, { useEffect, useRef, useState } from "react";
import { TitleBlock, Field, Input, SelectInput, Note, Actions, PrimaryBtn, TextBtn } from "../../../../auth/v2/pieces";
import { ScoreCard, Section, RowList, Toggle, CheckCircle, OutlineBadge, fmtRange, hasSocials, visibilityRead, visibilityUnread, visibilityHalf, confidenceLabel, UNREAD_COPY, socialCount, plural, EMPTY, US_STATE_ABBRS } from "../onboardingPieces";
import { splitCityOnBlur, abbrStateOptions } from "../hometownFields";
import { formatHometown } from "../../../../../utils/hometown";
import { usePhoneTheme } from "../../../../ForBrands/useIsPhone";
import { coverageOf, coverageSentence } from "../../../teamCoverage";
import { fmtIndex10 } from "../../../../../utils/indexScore";
import { RateCard } from "./PlanSteps";

// On phones (the AuthFrame phone theme, below 640px) these steps follow
// ATHLETE MODE V2 (mobile): the ScoreCard turns into the compact card with
// the Confidence badge plus the halves card by itself (D-o5), Contribution
// shows its two halves side by side, the "Unlock later" rows are greyed with
// a lock (13 1573:551, 17 1574:623), and the detail fields share the column
// (18 1574:506). Rows that need data the onboarding state does not carry
// (brand category fit) stay the web's (D-o9). 640px and up is unchanged.

const missing = (v) => v == null || v === "" || Number.isNaN(Number(v));
// A pillar (Performance, Visibility, IRL impact) out of 10, "6.4/10"
// (utils/indexScore); "Not yet" before it is computed.
const index10 = (v) => (missing(v) ? EMPTY : fmtIndex10(Number(v), { suffix: true }));
// The CacheScore stays a plain number out of 100, never a percent.
const scoreText = (v) => (missing(v) ? EMPTY : String(Math.round(Number(v))));

// Phones (1690:1234): Contribution is the Performance / Visibility split in
// a column of its own; the CacheScore it adds up to is on the ring above.
const includedRows = (state, phone = false) => {
  const m = state.metrics || {};
  const s = state.social || {};
  return [
    phone
      ? { title: "Contribution", pairs: [{ label: "Performance", value: index10(m.performance) }, { label: "Visibility", value: index10(m.visibility) }] }
      : { title: "Contribution", sub: `Performance ${index10(m.performance)} · Visibility ${index10(m.visibility)}`, value: scoreText(m.cacheScore) },
    { title: "Brand engagement", sub: "vs 3.4% benchmark", value: s.engagementRate != null ? `${(Number(s.engagementRate) * (s.engagementRate < 1 ? 100 : 1)).toFixed(1)}%` : hasSocials(state) ? EMPTY : "Not connected" },
    { title: "IRL impact", sub: "Awards, activities and service", value: index10(m.irl) },
  ];
};

const LOCKED_ROWS = [
  { title: "CacheValue", sub: "What a brand would pay you", chevron: true },
  { title: "CacheNILx Value", sub: "Your NIL market value", chevron: true },
  { title: "NILx Rate Card", sub: "Price per post, reel and event", chevron: true },
];

// The state's saved hometown as the profile shows it ("Las Vegas, NV"): the
// city with the postal code of the state, read from the codes list (a
// state is stored as its code). The city alone while no list is at hand,
// the state name when only a state is saved, else "".
const stateCodeMap = (codes) => ({
  state: Object.fromEntries((Array.isArray(codes) ? codes : []).filter((c) => c && c.type === "state" && c.value != null).map((c) => [c.value, c])),
});
export const onboardingHometown = (state, codes = []) => {
  const d = state?.details || {};
  return formatHometown({ homeTown: d.homeTown || "", state: d.state }, stateCodeMap(codes));
};

// The hero line under the name on 13 and 17: position, jersey and team.
// Phones add the hometown when one is saved (1573:551, 1574:623: "G · #10
// · Bishop Gorman · Las Vegas, NV"); 640px and up keep the line without
// it. `codes` is the codes list (state.codes.allCodes, or the details
// step's state rows).
export const liveHeroSub = (state, { phone = false, codes = [] } = {}) => {
  const d = state?.details || {};
  return [(d.position || [])[0], d.jerseyNumber ? `#${d.jerseyNumber}` : "", state?.team?.teamName, phone ? onboardingHometown(state, codes) : ""]
    .filter(Boolean)
    .join(" · ");
};

// 13 · Profile live, Core metrics. Phones (1573:551): the "Unlock later"
// rows in tertiary text with a lock (1690:1250) in place of the faded rows,
// and the footer with its top rule (1690:1272). When Premium is included
// with the team's plan (state.coverage) there is nothing to unlock: the
// step says so and its button opens the Premium screen (onUnlock; the
// wizard routes it there).
export const ProfileLiveStep = ({ state, onUnlock, onProfile }) => {
  const phone = usePhoneTheme();
  // A linked account counts only once it was read (see visibilityRead).
  const vis = visibilityRead(state);
  const perf = !!state.performance?.source;
  const coverage = coverageOf(state);
  return (
    <>
      <ScoreCard
        score={state.metrics?.cacheScore || 0}
        title={vis && perf ? "Your CacheScore is live" : "Your CacheScore is building"}
        sub={vis && perf ? "Both halves are in, visibility from your socials, performance from your team." : visibilityUnread(state) ? `${UNREAD_COPY} Public accounts read best.` : "Add the missing half any time from your profile."}
        confidence={confidenceLabel(state)}
        visibility={visibilityHalf(state)}
        performance={{ state: perf ? "on" : "warn", note: perf ? (state.performance?.source === "manual" ? "Self-reported" : "From team data") : "Awaiting stats" }}
      />
      <Section label="Included for free"><RowList rows={includedRows(state, phone)} /></Section>
      {coverage ? (
        <Section label="Included with your team">
          <Note wide>{coverageSentence(coverage)} CacheValue, CacheNILx and your NILx rate card are unlocked.</Note>
        </Section>
      ) : (
        <Section label="Unlock later" tertiary={phone}>
          {phone ? <RowList locked rows={LOCKED_ROWS} /> : <RowList muted rows={LOCKED_ROWS} />}
        </Section>
      )}
      <Actions rule={phone}>
        <PrimaryBtn onClick={onUnlock}>{coverage ? "See my Premium metrics" : "Unlock the rest"}</PrimaryBtn>
        <TextBtn onClick={onProfile}>Go to my profile</TextBtn>
      </Actions>
    </>
  );
};

// 17 · Optimized metrics. The values and the rate card show only once the
// account is Premium; a payment that has not landed yet shows a note instead.
export const isPremium = (state) => state?.entitlement === "athlete_premium";
export const valueRows = (nil = {}) => [
  { title: "CacheValue", sub: "What a brand would pay you", value: fmtRange(nil.cacheValue ?? nil.value ?? nil.cache_value) },
  { title: "CacheNILx Value", sub: nil.nilxValue?.tier || "Your NIL market value", value: fmtRange(nil.nilxValue ?? nil.nilx ?? nil.cache_nilx) },
];

// Phones (1574:623): the compact card with the Confidence badge; the web's
// section order and value ranges stay (D-o10, D-o11).
export const OptimizedStep = ({ state, onProfile, onCircle, pending = false }) => {
  const phone = usePhoneTheme();
  const nil = state.metrics?.nilValuation || {};
  const premium = isPremium(state);
  // Premium included with the team's plan says whose plan it is.
  const coverage = premium ? coverageOf(state) : null;
  return (
    <>
      <ScoreCard
        chip={premium ? "Optimized metrics" : "Core metrics"}
        chipDot={premium ? "orange" : "blue"}
        score={state.metrics?.cacheScore || 0}
        ringLabel={(state.metrics?.cacheScore || 0) >= 70 ? "high" : "of 100"}
        highlight={false}
        title="CacheScore™"
        sub={premium ? "Position rank, brand fit and NILx value are now included." : "Position rank, brand fit and NILx value unlock with Premium."}
        confidence={confidenceLabel(state)}
        visibility={visibilityHalf(state)}
        performance={{ state: state.performance?.source ? "on" : "warn", note: state.performance?.source ? "From team data" : "Awaiting stats" }}
      />
      {!premium && (
        <Note wide>{pending ? "We are confirming your payment. Your Optimized numbers show here and on your profile once it clears." : "Optimized numbers are part of Premium."}</Note>
      )}
      {coverage && <Note wide>{coverageSentence(coverage)}</Note>}
      {premium && (
        <>
          <Section label="Now unlocked"><RowList rows={valueRows(nil)} /></Section>
          <RateCard rates={state.metrics?.nilRates} />
        </>
      )}
      <Section label="Still included"><RowList rows={includedRows(state, phone)} /></Section>
      <Actions>
        <PrimaryBtn onClick={onProfile}>Go to my profile</PrimaryBtn>
        <TextBtn onClick={onCircle}>Set up My Circle</TextBtn>
      </Actions>
    </>
  );
};

// The server takes plain numbers for these; say which one is off before saving.
const NUMBER_CHECKS = [
  ["jerseyNumber", /^\d+$/, "Enter your jersey number as digits only, for example 10."],
  ["height", /^\d+(\.\d+)?$/, "Enter your height in inches, for example 74."],
  ["weight", /^\d+(\.\d+)?$/, "Enter your weight in pounds, for example 180."],
];

const POSITIONS = ["Guard", "Point guard", "Shooting guard", "Forward", "Small forward", "Power forward", "Center", "Wing", "Other"];
const years = () => { const y = new Date().getFullYear(); return Array.from({ length: 8 }, (_, i) => y + i); };

const FALLBACK_STATES = abbrStateOptions(US_STATE_ABBRS);
const hasState = (v) => v !== undefined && v !== null && String(v).trim() !== "" && String(v).trim() !== "0";

// A position name as the picker lists it ("Point Guard" from the server is
// "Point guard" here); a name the list does not have is kept as it is.
const positionOption = (name) => {
  const text = String(name || "").trim();
  if (!text) return "";
  return POSITIONS.find((p) => p.toLowerCase() === text.toLowerCase()) || text;
};
const positionChoices = (value) => (value && !POSITIONS.includes(value) ? [...POSITIONS, value] : POSITIONS);

// The saved details as the form shows them. The form keeps them as its
// baseline too: a save sends only the fields that differ from it, so a value
// the athlete never touched (one the server pre-filled from their team's
// roster, say) is never written back as 0.
export const detailsForm = (d = {}) => ({
  position: positionOption((d.position || [])[0]),
  jerseyNumber: d.jerseyNumber ? String(d.jerseyNumber) : "",
  height: d.height ? String(d.height) : "",
  weight: d.weight ? String(d.weight) : "",
  gradYear: d.gradYear ? String(d.gradYear) : "",
  homeTown: d.homeTown || "",
  state: hasState(d.state) ? String(d.state) : "",
});
const DETAIL_KEYS = Object.keys(detailsForm());
const sameDetail = (a, b) => String(a ?? "").trim() === String(b ?? "").trim();

// 18 · Add details (enrich). The hometown is captured as a city plus a
// State picker (the state codes when they are loaded, else abbreviations,
// which the server resolves to the code). Phones (1574:506): position and
// jersey as equal halves, height and weight likewise, the city beside the
// 126px picker (D-o17), and no Skip in the footer (the header has it).
// `onReload` reads the onboarding state again when the step opens: the
// server may have pre-filled the profile since the wizard last read it
// (the stats refresh after a team link), and the fields the athlete has not
// touched show what it has now. A save sends only the fields the athlete
// changed.
export const DetailsStep = ({ state, onSave, onSkip, stateOptions = [], onReload }) => {
  const phone = usePhoneTheme();
  const [base, setBase] = useState(() => detailsForm(state.details || {}));
  const [form, setForm] = useState(base);
  const touched = useRef(new Set());
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const states = stateOptions.length ? stateOptions : FALLBACK_STATES;
  useEffect(() => {
    if (typeof onReload !== "function") return undefined;
    let live = true;
    (async () => {
      try {
        const fresh = await onReload();
        if (!live || !fresh) return;
        const next = detailsForm(fresh.details || {});
        setBase(next);
        setForm((f) => Object.fromEntries(DETAIL_KEYS.map((k) => [k, touched.current.has(k) ? f[k] : next[k]])));
      } catch { /* the form keeps the details it opened with */ }
    })();
    return () => { live = false; };
    // Only when the step opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const set = (k) => (e) => {
    const value = e.target.value;
    touched.current.add(k);
    setForm((f) => ({ ...f, [k]: value }));
  };
  // "Chandler, AZ" typed in the city box: the state moves to the picker
  // when it is empty (the same rule applies once more on save).
  const splitCity = (f) => {
    const out = splitCityOnBlur(f.homeTown, f.state, states);
    return out ? { ...f, ...out, state: out.state !== undefined ? String(out.state) : f.state } : f;
  };
  const splitOnBlur = () => setForm((f) => {
    const out = splitCity(f);
    if (!sameDetail(out.state, f.state)) touched.current.add("state");
    return out;
  });
  const save = async () => {
    const problem = NUMBER_CHECKS.find(([k, re]) => { const v = String(form[k] ?? "").trim(); return v && !re.test(v); });
    if (problem) { setError(problem[2]); return; }
    const f = splitCity(form);
    const changed = (k) => !sameDetail(f[k], base[k]);
    setBusy(true); setError("");
    try {
      const stateValue = /^\d+$/.test(f.state) ? Number(f.state) : f.state;
      const out = {};
      if (changed("position")) out.position = f.position ? [f.position] : [];
      ["jerseyNumber", "height", "weight", "gradYear"].forEach((k) => { if (changed(k)) out[k] = f[k] || 0; });
      // The city and its state are one hometown: either changing sends both
      // (the server reads a state typed into the city against the picker).
      if (changed("homeTown") || changed("state")) {
        out.homeTown = String(f.homeTown || "").trim();
        if (hasState(f.state)) out.state = stateValue;
      }
      await onSave(out);
    } catch (e) { setError(e.message || "We could not save your details."); } finally { setBusy(false); }
  };
  return (
    <>
      <TitleBlock eyebrow="Enrich" title="Add details" sub="These fill out your public profile. Brands see them when they discover you. None of it blocks your score." />
      <div className={phone ? "grid grid-cols-2 items-start gap-[7px]" : "flex items-start gap-[10px]"}>
        <Field label="Position">
          <SelectInput className={phone ? "" : "w-[206px]"} value={form.position} onChange={set("position")} aria-label="Position"><option value="">Select</option>{positionChoices(form.position).map((p) => <option key={p}>{p}</option>)}</SelectInput>
        </Field>
        <Field label="Jersey #"><Input className={phone ? "" : "!w-[126px]"} inputMode="numeric" placeholder="10" value={form.jerseyNumber} onChange={set("jerseyNumber")} /></Field>
      </div>
      <Field label="Height and weight">
        <div className={phone ? "grid grid-cols-2 items-start gap-[10px]" : "flex items-start gap-[10px]"}>
          <Input className={phone ? "" : "!w-[166px]"} inputMode="numeric" placeholder="Height, inches" value={form.height} onChange={set("height")} aria-label="Height in inches" />
          <Input className={phone ? "" : "!w-[166px]"} inputMode="numeric" placeholder="Weight, lbs" value={form.weight} onChange={set("weight")} aria-label="Weight in pounds" />
        </div>
      </Field>
      {/* The high school class year, as the edit hub and profile name it. */}
      <Field label="High school graduation year">
        <SelectInput value={form.gradYear} onChange={set("gradYear")} aria-label="High school graduation year"><option value="">Select</option>{years().map((y) => <option key={y} value={y}>{y}</option>)}</SelectInput>
      </Field>
      <Field label="Hometown">
        <div className={phone ? "grid grid-cols-[minmax(0,1fr)_126px] items-start gap-[10px]" : "flex items-start gap-[10px]"}>
          <Input className={phone ? "" : "!w-[206px]"} placeholder="Las Vegas" value={form.homeTown} onChange={set("homeTown")} onBlur={splitOnBlur} aria-label="Hometown city" />
          <SelectInput className={phone ? "" : "w-[126px]"} value={form.state} onChange={set("state")} aria-label="Hometown state">
            <option value="">State</option>
            {states.map((o) => <option key={String(o.value)} value={String(o.value)}>{o.options?.abbr || o.name}</option>)}
          </SelectInput>
        </div>
      </Field>
      {error && <Note tone="critical">{error}</Note>}
      <Actions>
        <PrimaryBtn onClick={save} loading={busy}>Save and continue</PrimaryBtn>
        {!phone && <TextBtn onClick={onSkip}>Skip</TextBtn>}
        <p className="text-center bm-body-s text-bm-tertiary">You can edit any of this later from your profile.</p>
      </Actions>
    </>
  );
};

// 19 · Privacy controls.
export const PrivacyStep = ({ state, onSave, onCircle }) => {
  const [prefs, setPrefs] = useState({ ...(state.privacyPrefs || {}) });
  const [busy, setBusy] = useState(false);
  const rows = [
    { key: "cacheScorePublic", title: "CacheScore", sub: "Always visible. This is how brands find you", locked: true },
    { key: "cacheValueVisible", title: "CacheValue", sub: "Your current market value" },
    { key: "nilDealsVisible", title: "CacheNILx Value", sub: "Your NIL market value" },
    { key: "socialsVisible", title: "NILx Rate Card", sub: "Your personal rate card for NIL engagements. Price per post, reel and event" },
  ];
  return (
    <>
      <TitleBlock eyebrow="Parallel · anytime" title="What brands can see" sub="Everyone always sees your CacheProfile. You choose which numbers appear inside it." />
      <div className="flex flex-col gap-[10px]">
        {rows.map((r) => (
          <div key={r.key} className={`flex items-center justify-between gap-4 rounded-[12px] p-4 ${r.locked ? "min-h-[108px] bg-bm-mutedbg" : "min-h-[114px] border border-bm-border bg-white"}`}>
            <div className="flex min-w-0 flex-col gap-1"><div className="bm-h-s text-bm-ink">{r.title}</div><div className="bm-body-m text-bm-muted">{r.sub}</div></div>
            {r.locked ? <OutlineBadge>Always on</OutlineBadge> : <Toggle on={!!prefs[r.key]} onChange={(v) => setPrefs({ ...prefs, [r.key]: v })} />}
          </div>
        ))}
      </div>
      <Note wide>Hiding a number does not lower it. Brands still rank you on your CacheScore. They just will not see your price until you share it.</Note>
      <Actions>
        <PrimaryBtn loading={busy} onClick={async () => { setBusy(true); try { await onSave(prefs); } finally { setBusy(false); } }}>Save</PrimaryBtn>
        <TextBtn onClick={onCircle}>Set up My Circle</TextBtn>
      </Actions>
    </>
  );
};

// 21 · Resume from notification. Phones (1761:1476): the compact card with
// the Confidence badge (none while the ring is muted); the web's title, sub
// and "accounts added" stay.
const PERFORMANCE_STEPS = ["performance", "seasonStats", "noData", "performanceDone"];

export const ResumeStep = ({ state, savedStep = "", onContinue, onNotNow, notNowLabel = "Not now" }) => {
  // Links saved counts as the step done; visibility is claimed only once read.
  const socialsDone = hasSocials(state);
  const visRead = visibilityRead(state);
  const teamDone = !!(state.team?.teamName || (state.team?.verifiedTeams || []).length);
  const photoDone = !!state.avatar;
  // A team is on file but the athlete left while supplying game data: go back there.
  const statsPending = teamDone && !state.performance?.source && PERFORMANCE_STEPS.includes(savedStep);
  const next = !socialsDone ? "socials" : !teamDone ? "team" : statsPending ? "stats" : !photoDone ? "photo" : "live";
  // No number on the ring yet, so no confidence badge beside it either.
  const muted = !(state.metrics?.cacheScore > 0);
  const label = { socials: "Add my socials", team: "Add my team code", stats: "Add my game stats", photo: "Add a photo", live: "See my profile" }[next];
  const items = [
    { title: "Social profiles", sub: socialsDone ? `${plural(socialCount(state), "account")} added` : "Takes about a minute", done: socialsDone },
    { title: "Team code", sub: statsPending ? "Team added. Game stats still needed" : "Takes about 30 seconds", done: teamDone },
    { title: "Profile photo", sub: "Optional", done: photoDone },
  ];
  return (
    <>
      <TitleBlock eyebrow="Pick up where you left off" title={visRead && !teamDone ? "Half your score is waiting" : "Finish your profile"} sub={visRead && !teamDone ? "Your visibility is in. Add your team and the performance half calculates too." : "A few steps remain before your CacheScore is complete."} />
      <ScoreCard
        score={state.metrics?.cacheScore || 0}
        highlight={false}
        muted={muted}
        chip="Core metrics"
        title={state.metrics?.cacheScore ? "Partial score" : "No score yet"}
        sub={visRead ? "Visibility is in. Performance is waiting on game data from your team." : socialsDone ? UNREAD_COPY : "Connect a social account or add a team and the score starts building."}
        confidence={muted ? null : confidenceLabel(state)}
        visibility={visibilityHalf(state)}
        performance={{ state: teamDone && state.performance?.source ? "on" : "warn", note: teamDone && state.performance?.source ? "From team data" : "Awaiting stats" }}
      />
      <Section label="What is left">
        <div className="flex flex-col gap-[10px]">
          {items.map((it) => (
            <div key={it.title} className={`flex items-center justify-between gap-3 rounded-[12px] bg-bm-subtle px-4 py-[14px] ${it.done ? "min-h-[98px]" : "min-h-[132px]"}`}>
              <div className="flex items-center gap-3">
                {it.done ? <CheckCircle size={22} /> : <span className="h-[22px] w-[22px] rounded-full bg-bm-mutedbg" />}
                <div className="flex flex-col gap-[3px]"><div className="bm-label-l text-bm-ink">{it.title}</div><div className="bm-body-s text-bm-muted">{it.sub}</div></div>
              </div>
              {!it.done && <OutlineBadge dot="yellow">To do</OutlineBadge>}
            </div>
          ))}
        </div>
      </Section>
      <Actions>
        <PrimaryBtn onClick={() => onContinue(next)}>{label}</PrimaryBtn>
        <TextBtn onClick={onNotNow}>{notNowLabel}</TextBtn>
      </Actions>
    </>
  );
};
